import React, { useEffect, useState } from 'react';
import { getLoadProfiles, getCapitalCheck } from '../../services/SettingsService';
import { getEnvironment } from '../../services/EnvironmentStorage';

const PROFILE_LABELS = { ECONOMIC: '🟢 Econômico', BALANCED: '🟡 Equilibrado', AGGRESSIVE: '🔴 Agressivo' };
const PROFILE_ORDER = ['ECONOMIC', 'BALANCED', 'AGGRESSIVE'];

// Níveis de impacto (0–5) por perfil, só pra desenhar as barrinhas.
const SERVER_IMPACT = {
    ECONOMIC:   { ram: 1, cpu: 1 },
    BALANCED:   { ram: 3, cpu: 2 },
    AGGRESSIVE: { ram: 4, cpu: 3 },
};
const AI_TOKEN_BAR = { ECONOMIC: 2, BALANCED: 3, AGGRESSIVE: 4 };

function Bar({ level, max = 5, color }) {
    return (
        <div className='d-flex gap-1 align-items-center' style={{ height: 8 }}>
            {Array.from({ length: max }).map((_, i) => (
                <span key={i} style={{ height: 6, flex: 1, borderRadius: 2, background: i < level ? color : '#e9ecef' }} />
            ))}
        </div>
    );
}

function ProfileButtons({ profiles, current, recommended, onSelect }) {
    return (
        <div className='btn-group w-100 mb-3' role='group'>
            {PROFILE_ORDER.filter(k => profiles[k]).map(k => (
                <button key={k} type='button'
                    className={`btn btn-sm ${current === k ? 'btn-primary' : 'btn-outline-primary'} position-relative`}
                    onClick={() => onSelect(k)}>
                    {PROFILE_LABELS[k]}
                    {recommended === k && (
                        <span className='badge bg-success ms-1' style={{ fontSize: '0.6rem' }}>🎯</span>
                    )}
                </button>
            ))}
        </div>
    );
}

/**
 * props:
 * - serverProfile, aiProfile, aiTopN  (valores atuais)
 * - onChange(field, value)    ('serverProfile'|'aiProfile'|'aiTopN')
 */
function LoadProfiles({ serverProfile, aiProfile, aiTopN, onChange }) {
    const [meta, setMeta] = useState(null);
    const [cap, setCap] = useState(null);
    const server = serverProfile || 'BALANCED';
    const ai = aiProfile || 'ECONOMIC';

    // Selecionar um perfil de Servidor pré-preenche o aiTopN (o knob único de runtime,
    // usado pelo scanner e pelo orquestrador). O usuário ainda pode ajustar o slider.
    function selectServer(p) {
        onChange('serverProfile', p);
        const topN = meta?.server?.profiles?.[p]?.topN;
        if (topN) onChange('aiTopN', topN);
    }

    useEffect(() => {
        getLoadProfiles().then(setMeta).catch(() => { /* silencioso */ });
    }, []);

    // topN EFETIVO = o knob de runtime (aiTopN) se houver, senão o do perfil de Servidor.
    const effectiveTopN = (aiTopN != null ? aiTopN : meta?.server?.profiles?.[server]?.topN) || 1;

    // Checagem advisory de viabilidade de capital — refaz quando o topN efetivo muda. Fail-open:
    // erro / sem credencial = { available:false } → o card simplesmente não aparece.
    useEffect(() => {
        if (!meta) return;
        let alive = true;
        getCapitalCheck({ topN: effectiveTopN, type: 'grid', environment: getEnvironment() || 'production' })
            .then(d => { if (alive) setCap(d); })
            .catch(() => { if (alive) setCap(null); });
        return () => { alive = false; };
    }, [meta, effectiveTopN]);

    if (!meta) return null;

    const tokensPerDay = meta.estimates?.[server]?.[ai] ?? null;
    // Capacidade GRÁTIS do POOL (sharding/fallback) = soma dos free diários das IAs do pool de sinal,
    // não o free de UM provedor. Fora do pool entram pagas (capacidade extra) e grátis sem teto de
    // tokens documentado (limita por requisição) — ambas dão folga além do número somado.
    const pool = meta.ai.poolCapacity || {};
    const freeCap = pool.freeDailyTokens || 0;
    const poolSize = pool.poolSize || 0;
    const hasHeadroom = (pool.unmeteredFree || 0) > 0 || (pool.paid || 0) > 0;
    const sharded = meta.ai.schedulingMode === 'SHARDED' && poolSize > 1;
    const fitsKnownFree = freeCap > 0 && tokensPerDay !== null && tokensPerDay <= freeCap;
    // "estoura" só se passa do grátis CONHECIDO e não há nenhuma folga (sem teto / paga) no pool.
    const exceedsFree = tokensPerDay !== null && !fitsKnownFree && !hasHeadroom;
    const capTxt = freeCap > 0 ? `${freeCap.toLocaleString('pt-BR')} tokens/dia grátis` : null;
    const extraTxt = (pool.unmeteredFree || 0) > 0 ? ` + ${pool.unmeteredFree} sem teto de tokens` : '';
    const srvImpact = SERVER_IMPACT[server] || SERVER_IMPACT.BALANCED;
    const hw = meta.server?.hardware || {};

    return (
        <div className='row'>
            {/* Card Servidor */}
            <div className='col-md-6 mb-3'>
                <div className='card border-0 shadow-sm h-100'>
                    <div className='card-body'>
                        <h6 className='fw-bold mb-2'>🖥️ Perfil de Servidor <small className='text-muted'>(RAM / CPU)</small></h6>
                        <ProfileButtons profiles={meta.server.profiles} current={server}
                            recommended={meta.server.recommended} onSelect={selectServer} />

                        <div className='mb-2'>
                            <small className='text-muted'>RAM no servidor</small>
                            <Bar level={srvImpact.ram} color='#0d6efd' />
                        </div>
                        <div className='mb-2'>
                            <small className='text-muted'>CPU</small>
                            <Bar level={srvImpact.cpu} color='#6610f2' />
                        </div>
                        {/* Parâmetro REAL em foco: quantos ativos rodam ao mesmo tempo. */}
                        <div className='d-flex align-items-baseline gap-2 mt-3'>
                            <span className='fs-2 fw-bold lh-1 text-primary'>{effectiveTopN}</span>
                            <span className='text-muted'>ativo(s) simultâneo(s)</span>
                        </div>
                        <small className='text-muted d-block'>
                            monitores {meta.server.profiles[server]?.monitorSet === 'LEAN' ? 'enxutos' : 'completos'}.
                        </small>
                        {/* Viabilidade de capital (advisory; não bloqueia). Some se indisponível. */}
                        {cap?.available && (
                            cap.fits ? (
                                <div className='alert alert-success py-1 px-2 small mt-2 mb-0'>
                                    ✅ Saldo livre ({cap.balanceQuote.toLocaleString('pt-BR')} {cap.quote}) comporta os {cap.topN} ativo(s) — fatia mín. ~{cap.minSlice.toLocaleString('pt-BR')} {cap.quote} cada.
                                </div>
                            ) : (
                                <div className='alert alert-warning py-1 px-2 small mt-2 mb-0'>
                                    ⚠️ O saldo livre ({cap.balanceQuote.toLocaleString('pt-BR')} {cap.quote}) sustenta ~{cap.maxViableSlots} de {cap.topN} ativo(s) acima do mínimo. Pra {cap.topN}, faltam ~{cap.shortfall.toLocaleString('pt-BR')} {cap.quote} — depositar ou converter pra {cap.quote}?
                                </div>
                            )
                        )}
                        {hw.totalRamMB ? (
                            <small className='text-muted d-block'>
                                Detectado: {(hw.totalRamMB / 1024).toFixed(1)} GB RAM · {hw.cpus} CPU(s).
                                {meta.server.recommended !== server && (
                                    <> 🎯 Recomendado: <strong>{PROFILE_LABELS[meta.server.recommended]}</strong>.</>
                                )}
                            </small>
                        ) : null}
                        {aiTopN != null && meta.server.profiles[server] && aiTopN !== meta.server.profiles[server].topN && (
                            <div className='alert alert-warning py-1 px-2 small mt-2 mb-0'>
                                ⚠️ O Top-N do scanner está em <strong>{aiTopN}</strong>, diferente do perfil {PROFILE_LABELS[server]} (sugere {meta.server.profiles[server].topN}). O scanner e o orquestrador usam o Top-N — ajuste-o na aba IA, ou reescolha o perfil pra alinhar.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Card IA */}
            <div className='col-md-6 mb-3'>
                <div className='card border-0 shadow-sm h-100'>
                    <div className='card-body'>
                        <h6 className='fw-bold mb-2'>🧠 Perfil de IA <small className='text-muted'>(tokens)</small></h6>
                        <ProfileButtons profiles={meta.ai.profiles} current={ai}
                            recommended={meta.ai.recommended} onSelect={v => onChange('aiProfile', v)} />

                        <div className='mb-2'>
                            <small className='text-muted'>Consumo de tokens/dia</small>
                            <Bar level={AI_TOKEN_BAR[ai] || 3} color={exceedsFree ? '#dc3545' : '#198754'} />
                        </div>
                        {tokensPerDay !== null && (
                            <small className='text-muted d-block'>
                                ≈ {tokensPerDay.toLocaleString('pt-BR')} tokens/dia <span className='text-muted'>(com o servidor atual)</span>.
                            </small>
                        )}
                        {/* Parâmetro REAL em foco: de quanto em quanto tempo a IA analisa. */}
                        <div className='d-flex align-items-baseline gap-2 mt-3'>
                            <span className='fs-2 fw-bold lh-1 text-success'>{(meta.ai.profiles[ai]?.aiSchedule || '').replace('*/', '').split(' ')[0]}</span>
                            <span className='text-muted'>min entre análises</span>
                        </div>
                        <small className='text-muted d-block'>
                            confirma o sinal por {meta.ai.profiles[ai]?.consensusCycles} ciclo(s).
                        </small>
                        {sharded && poolSize > 0 && (
                            <small className='text-info d-block mt-1'>
                                ⇄ Distribuído entre {poolSize} IA{poolSize > 1 ? 's' : ''} do pool — a carga se divide entre elas.
                            </small>
                        )}
                        {poolSize === 0 ? (
                            <div className='alert alert-secondary py-1 px-2 small mt-2 mb-0'>
                                Cadastre ao menos um provedor de IA para estimar a capacidade.
                            </div>
                        ) : exceedsFree ? (
                            <div className='alert alert-warning py-1 px-2 small mt-2 mb-0'>
                                ⚠️ Passa da capacidade grátis combinada do pool{capTxt ? ` (~${capTxt}${extraTxt})` : ''} —
                                adicione outra IA grátis ao pool, reduza a cadência, ou use IA paga (o fallback cobre, mas com mais latência).
                            </div>
                        ) : (
                            <div className='alert alert-success py-1 px-2 small mt-2 mb-0'>
                                ✅ Cabe na capacidade grátis do pool{capTxt ? ` (~${capTxt}${extraTxt}, ${poolSize} IA${poolSize > 1 ? 's' : ''})` : `${hasHeadroom ? ' (IAs sem teto de tokens / pagas no pool)' : ''}`}.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoadProfiles;
